<script setup>
import { computed, ref } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  activities: {
    type: Object,
    default: () => ({}),
  },
  notes: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const activeTab = ref('activities');

function formatNumber(num) {
  if (!num) return '0';
  return num.toLocaleString('pt-BR');
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}min atras`;
  if (diffHours < 24) return `${diffHours}h atras`;
  if (diffDays < 7) return `${diffDays}d atras`;
  return date.toLocaleDateString('pt-BR');
}

function getActivityIcon(type) {
  const icons = {
    stage_changed: 'i-lucide-git-branch',
    deal_status_changed: 'i-lucide-check-circle',
    value_updated: 'i-lucide-dollar-sign',
    probability_updated: 'i-lucide-percent',
    assigned: 'i-lucide-user-plus',
    unassigned: 'i-lucide-user-minus',
    note_added: 'i-lucide-sticky-note',
    product_added: 'i-lucide-package',
    appointment_created: 'i-lucide-calendar',
    checklist_item_added: 'i-lucide-list-checks',
  };
  return icons[type] || 'i-lucide-activity';
}

function getActivityColor(type) {
  const colors = {
    stage_changed: 'text-n-blue-10',
    deal_status_changed: 'text-n-teal-10',
    value_updated: 'text-n-amber-10',
    probability_updated: 'text-n-violet-10',
    assigned: 'text-n-cyan-10',
    unassigned: 'text-n-ruby-10',
    note_added: 'text-n-slate-10',
    product_added: 'text-n-orange-10',
    appointment_created: 'text-n-pink-10',
    checklist_item_added: 'text-n-lime-10',
  };
  return colors[type] || 'text-n-slate-10';
}

function getNoteTypeColor(type) {
  const colors = {
    general: 'bg-n-slate-3 text-n-slate-11',
    important: 'bg-n-ruby-3 text-n-ruby-11',
    follow_up: 'bg-n-amber-3 text-n-amber-11',
    internal: 'bg-n-blue-3 text-n-blue-11',
  };
  return colors[type] || 'bg-n-slate-3 text-n-slate-11';
}

function getNoteTypeLabel(type) {
  const labels = {
    general: 'Geral',
    important: 'Importante',
    follow_up: 'Followup',
    internal: 'Interno',
  };
  return labels[type] || type;
}

const recentActivities = computed(() => props.activities?.recent || []);
const byType = computed(() => props.activities?.by_type || {});
const recentNotes = computed(() => props.notes?.recent || []);
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex gap-1">
        <button
          class="px-3 py-1.5 text-sm rounded transition-colors flex items-center gap-2"
          :class="
            activeTab === 'activities'
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeTab = 'activities'"
        >
          <span class="i-lucide-activity" />
          Atividades
        </button>
        <button
          class="px-3 py-1.5 text-sm rounded transition-colors flex items-center gap-2"
          :class="
            activeTab === 'notes'
              ? 'bg-n-blue-9 text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="activeTab = 'notes'"
        >
          <span class="i-lucide-sticky-note" />
          Notas
        </button>
      </div>
    </div>

    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <!-- Activities Tab -->
    <div v-else-if="activeTab === 'activities'" class="flex flex-col gap-4">
      <!-- Summary -->
      <div class="grid grid-cols-2 gap-3">
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Total Atividades</div>
          <div class="text-xl font-bold text-n-slate-12">
            {{ formatNumber(activities.total) }}
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Hoje</div>
          <div class="text-xl font-bold text-n-blue-11">
            {{ formatNumber(activities.today) }}
          </div>
        </div>
      </div>

      <!-- By Type -->
      <div v-if="Object.keys(byType).length > 0" class="flex flex-wrap gap-2">
        <div
          v-for="(count, type) in byType"
          :key="type"
          class="flex items-center gap-1 px-2 py-1 rounded-lg bg-n-alpha-1 text-xs"
        >
          <span :class="[getActivityIcon(type), getActivityColor(type)]" />
          <span class="text-n-slate-12">{{ formatNumber(count) }}</span>
        </div>
      </div>

      <!-- Recent Activities -->
      <div v-if="recentActivities.length > 0" class="flex flex-col gap-2">
        <div class="text-xs font-medium text-n-slate-11">
          Atividades Recentes
        </div>
        <div
          v-for="activity in recentActivities"
          :key="activity.id"
          class="flex items-start gap-3 p-3 rounded-lg bg-n-alpha-1"
        >
          <span
            class="text-lg mt-0.5"
            :class="[
              getActivityIcon(activity.activity_type),
              getActivityColor(activity.activity_type),
            ]"
          />
          <div class="flex-1 flex flex-col">
            <span class="text-sm text-n-slate-12">{{
              activity.description
            }}</span>
            <div class="flex items-center gap-2 text-xs text-n-slate-10">
              <span>{{ activity.performed_by }}</span>
              <span>•</span>
              <span>{{ formatDate(activity.created_at) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="recentActivities.length === 0 && !activities.total"
        class="text-center text-n-slate-11 py-4"
      >
        Nenhuma atividade registrada
      </div>
    </div>

    <!-- Notes Tab -->
    <div v-else-if="activeTab === 'notes'" class="flex flex-col gap-4">
      <!-- Summary -->
      <div class="grid grid-cols-2 gap-3">
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Total Notas</div>
          <div class="text-xl font-bold text-n-slate-12">
            {{ formatNumber(notes.total) }}
          </div>
        </div>
        <div class="p-3 rounded-lg bg-n-alpha-1 text-center">
          <div class="text-xs text-n-slate-10 mb-1">Esta Semana</div>
          <div class="text-xl font-bold text-n-blue-11">
            {{ formatNumber(notes.this_week) }}
          </div>
        </div>
      </div>

      <!-- By Type -->
      <div
        v-if="notes.by_type && Object.keys(notes.by_type).length > 0"
        class="flex flex-wrap gap-2"
      >
        <div
          v-for="(count, type) in notes.by_type"
          :key="type"
          class="px-3 py-1.5 rounded-lg text-xs font-medium"
          :class="getNoteTypeColor(type)"
        >
          {{ getNoteTypeLabel(type) }}: {{ formatNumber(count) }}
        </div>
      </div>

      <!-- Recent Notes -->
      <div v-if="recentNotes.length > 0" class="flex flex-col gap-2">
        <div class="text-xs font-medium text-n-slate-11">Notas Recentes</div>
        <div
          v-for="note in recentNotes"
          :key="note.id"
          class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-1"
        >
          <div class="flex items-center justify-between">
            <span
              class="text-xs px-2 py-0.5 rounded"
              :class="getNoteTypeColor(note.note_type)"
            >
              {{ getNoteTypeLabel(note.note_type) }}
            </span>
            <span class="text-xs text-n-slate-10">{{
              formatDate(note.created_at)
            }}</span>
          </div>
          <p class="text-sm text-n-slate-12 line-clamp-2">{{ note.content }}</p>
          <div class="text-xs text-n-slate-11">{{ note.author }}</div>
        </div>
      </div>

      <div
        v-if="recentNotes.length === 0 && !notes.total"
        class="text-center text-n-slate-11 py-4"
      >
        Nenhuma nota registrada
      </div>
    </div>
  </div>
</template>
